import { useState } from 'react';
import {
  useDeceptionAssets,
  useDeceptionAlerts,
  deployDecoy,
  removeDecoy,
  type DeceptionAsset,
} from '../../hooks/useSecOps';

const STATUS_COLORS: Record<string, string> = {
  deploying: 'bg-blue-500/20 text-blue-400',
  active: 'bg-green-500/20 text-green-400',
  triggered: 'bg-red-500/20 text-red-400',
  removed: 'bg-zinc-700 text-zinc-400',
  failed: 'bg-red-500/20 text-red-400',
};

const DECOY_TYPE_ICONS: Record<string, { icon: string; label: string; color: string }> = {
  honey_file: { icon: '📄', label: 'Honey File', color: 'bg-amber-500/20 text-amber-400' },
  honey_cred: { icon: '🔑', label: 'Honey Credential', color: 'bg-purple-500/20 text-purple-400' },
  honey_service: { icon: '🌐', label: 'Honey Service', color: 'bg-cyan-500/20 text-cyan-400' },
  honey_share: { icon: '📁', label: 'Honey Share', color: 'bg-emerald-500/20 text-emerald-400' },
};

type DecoyType = 'honey_file' | 'honey_cred' | 'honey_service' | 'honey_share';

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-zinc-700 text-zinc-400'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

function StatCard({ label, value, accent, suffix }: { label: string; value: number | string; accent: string; suffix?: string }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
      <div className="flex items-baseline gap-1">
        <p className="text-3xl font-bold text-gray-100">{value}</p>
        {suffix && <span className="text-sm text-gray-400">{suffix}</span>}
      </div>
      <p className="text-sm text-gray-400 mt-1">{label}</p>
      <div className={`mt-3 h-1 w-12 rounded ${accent}`} />
    </div>
  );
}

export default function DeceptionPage() {
  const { data: assetsResp, loading: assetsLoading, refresh: refreshAssets } = useDeceptionAssets();
  const { data: alertsResp, loading: alertsLoading } = useDeceptionAlerts();

  const assets = assetsResp?.data?.assets ?? [];
  const stats = assetsResp?.data?.stats;
  const alerts = alertsResp?.data?.alerts ?? [];

  const [deployModal, setDeployModal] = useState(false);
  const [decoyType, setDecoyType] = useState<DecoyType>('honey_file');
  const [targetDevice, setTargetDevice] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const handleDeploy = async () => {
    if (!targetDevice.trim()) return;
    setDeploying(true);
    try {
      await deployDecoy({ type: decoyType, target_device_id: targetDevice.trim() });
      setDeployModal(false);
      setTargetDevice('');
      refreshAssets();
    } finally {
      setDeploying(false);
    }
  };

  const handleRemove = async (asset: DeceptionAsset) => {
    setRemoving(asset.id);
    try {
      await removeDecoy(asset.id);
      refreshAssets();
    } finally {
      setRemoving(null);
    }
  };

  if (assetsLoading && assets.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-800 rounded w-72" />
          <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-800 rounded-lg" />)}</div>
          <div className="h-96 bg-zinc-800 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Deception Technology</h1>
          <p className="text-gray-400 text-sm mt-1">Deploy honeypots and canary tokens to detect attackers with zero false positives</p>
        </div>
        <button
          onClick={() => setDeployModal(true)}
          className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          Deploy Decoy
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Decoys" value={stats.total} accent="bg-indigo-500" />
          <StatCard label="Active" value={stats.active} accent="bg-green-500" />
          <StatCard label="Triggered (30d)" value={stats.triggered_this_month} accent="bg-red-500" />
          <StatCard label="False Positive Rate" value={`${stats.false_positive_rate}%`} accent="bg-emerald-500" />
        </div>
      )}

      {/* Deployed Decoys */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg mb-8">
        <div className="px-6 py-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-gray-100">Deployed Decoys</h2>
        </div>
        {assets.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No decoys deployed yet. Click "Deploy Decoy" to set up your first honeypot.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-zinc-800">
                  <th className="px-6 py-3 font-medium">Type</th>
                  <th className="px-6 py-3 font-medium">Target Device</th>
                  <th className="px-6 py-3 font-medium">Deployed</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Triggers</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => {
                  const typeInfo = DECOY_TYPE_ICONS[asset.decoy_type] ?? { icon: '?', label: asset.decoy_type, color: 'bg-zinc-700 text-zinc-400' };
                  return (
                    <tr key={asset.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{typeInfo.icon}</span>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-gray-400 font-mono text-xs">{asset.target_device_id.slice(0, 16)}...</td>
                      <td className="px-6 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {new Date(asset.deployed_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-3"><Badge value={asset.status} colors={STATUS_COLORS} /></td>
                      <td className="px-6 py-3">
                        <span className={`text-sm font-semibold ${asset.trigger_count > 0 ? 'text-red-400' : 'text-gray-600'}`}>
                          {asset.trigger_count}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        {asset.status !== 'removed' && (
                          <button
                            onClick={() => handleRemove(asset)}
                            disabled={removing === asset.id}
                            className="text-red-400 hover:text-red-300 text-xs font-medium disabled:opacity-50"
                          >
                            {removing === asset.id ? 'Removing...' : 'Remove'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deception Alerts */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-100">Trip Alerts</h2>
          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-medium">0% False Positive</span>
        </div>
        {alertsLoading && alerts.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading alerts...</div>
        ) : alerts.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No deception alerts triggered. When an attacker interacts with a decoy, it will appear here instantly.</div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {alerts.map((alert) => {
              const typeInfo = DECOY_TYPE_ICONS[alert.decoy_type] ?? { icon: '?', label: alert.decoy_type, color: 'bg-zinc-700 text-zinc-400' };
              return (
                <div key={alert.id} className="px-6 py-4 hover:bg-zinc-800/30">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-lg mt-0.5">{typeInfo.icon}</span>
                      <div>
                        <p className="text-sm text-gray-200 font-medium">{alert.details}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                          <span className="text-xs text-gray-500 font-mono">{alert.target_device_id.slice(0, 12)}...</span>
                          {alert.attacker_ip && (
                            <span className="text-xs text-red-400">IP: {alert.attacker_ip}</span>
                          )}
                          {alert.attacker_user && (
                            <span className="text-xs text-orange-400">User: {alert.attacker_user}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-red-500/20 text-red-400 rounded-full px-2 py-0.5 text-xs font-medium">P1</span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(alert.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Deploy Modal */}
      {deployModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setDeployModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Deploy Decoy</h3>

            <div className="mb-4">
              <label className="text-sm text-gray-300 block mb-2">Decoy Type</label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.entries(DECOY_TYPE_ICONS) as [DecoyType, typeof DECOY_TYPE_ICONS[string]][]).map(([type, info]) => (
                  <button
                    key={type}
                    onClick={() => setDecoyType(type)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-colors ${
                      decoyType === type
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{info.icon}</span>
                    <div>
                      <p className="text-xs font-medium text-gray-200">{info.label}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-2 p-3 bg-zinc-800 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-xs text-gray-300">
                {decoyType === 'honey_file' && 'Places a fake sensitive document (e.g., passwords.xlsx) that alerts when opened or accessed.'}
                {decoyType === 'honey_cred' && 'Plants a fake credential in Windows credential store. Alerts when the credential is used.'}
                {decoyType === 'honey_service' && 'Opens a fake service port (SSH/RDP/SMB) that alerts on any connection attempt.'}
                {decoyType === 'honey_share' && 'Creates a fake network share with canary files that alert when accessed.'}
              </p>
            </div>

            <div className="mb-4">
              <label className="text-sm text-gray-300 block mb-1">Target Device ID</label>
              <input
                type="text"
                value={targetDevice}
                onChange={(e) => setTargetDevice(e.target.value)}
                placeholder="Enter device ID from RMM"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeployModal(false)}
                className="text-gray-400 hover:text-gray-300 text-sm px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleDeploy}
                disabled={deploying || !targetDevice.trim()}
                className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {deploying ? 'Deploying...' : 'Deploy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
